import React, { useContext, useEffect, useRef } from 'react';
import {
  AccordionTheme,
  TextButton,
  TextButtonPriority,
  useTPAComponentsContext,
} from '@wix/wix-ui-tpa/cssVars';
import { st, classes } from '../../Widget.st.css';
import { ISettingsContextValue } from '@wix/tpa-settings';
import settingsParams from '../../../settingsParams';
import { useSettings } from '@wix/tpa-settings/react';
import { DATA_HOOKS } from '../../../../../constants/dataHooksElements';
import { TabItem } from '@wix/wix-ui-tpa';
import { useLayoutType } from '../../../../../hooks/useLayoutType';
import { isEditor, useTranslation } from '@wix/yoshi-flow-editor';
import { AppContext } from '../../SettingsProvider/SettingsProvider';

export interface VerticalCategoriesProps {
  categoriesToRender: TabItem[];
  activeTab: number;
  sideCategoriesPosition: boolean;
  setActiveTab: (index: number) => void;
}

const VerticalCategories = ({
  categoriesToRender,
  activeTab,
  sideCategoriesPosition,
  setActiveTab,
}: VerticalCategoriesProps) => {
  const { seo } = useTPAComponentsContext();
  const { t } = useTranslation();
  const { isEditor3 } = useContext(AppContext);
  const { itemAppearance } = useLayoutType(isEditor3);
  const {
    layout: { numberLayout },
  } = useLayoutType(isEditor3);
  const theme = numberLayout ? AccordionTheme.Box : itemAppearance;
  const tabListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!tabListRef.current) {
        return;
      }

      const currentTab = document.activeElement;
      if (currentTab && currentTab.role === 'tab') {
        let newTab: HTMLElement | null = null;
        switch (event.key) {
          case 'ArrowLeft':
          case 'ArrowUp':
            newTab = currentTab.previousElementSibling as HTMLElement;
            break;
          case 'ArrowRight':
          case 'ArrowDown':
            newTab = currentTab.nextElementSibling as HTMLElement;

            break;
          case 'Enter':
          case 'Space':
            const tabId = currentTab.getAttribute('data-tab-index');
            if (tabId) {
              setActiveTab(Number(tabId));
            }
            break;
        }
        if (newTab) {
          currentTab.setAttribute('tabindex', '-1');
          newTab.setAttribute('tabindex', '0');
          newTab.setAttribute('aria-current', 'true');
          currentTab.setAttribute('aria-current', 'false');
          newTab.focus();
        }
      }
    };
    const tabList = tabListRef.current;
    tabList?.addEventListener('keydown', handleKeyDown);

    return () => {
      if (tabList) {
        tabList.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [setActiveTab]);
  return (
    <div
      className={st(classes.Tabs, {
        isSideCategoriesPosition: sideCategoriesPosition,
        theme,
      })}
      data-hook={DATA_HOOKS.WIDGET_CATEGORIES_VERTICAL}
      ref={tabListRef}
      role="tablist"
      aria-orientation="vertical"
      aria-label={t('display.tab.categories.title')}
    >
      {categoriesToRender.map((category, index) => {
        return (
          <TextButton
            data-tab-index={index}
            aria-current={activeTab === index ? 'true' : undefined}
            tabIndex={activeTab === index ? 0 : -1}
            role="tab"
            as={seo ? 'a' : 'button'}
            key={`category-button-${index}`}
            priority={
              activeTab === index
                ? TextButtonPriority.link
                : TextButtonPriority.secondary
            }
            className={st(classes.underlineTextColor, {
              isLink: activeTab === index,
            })}
            onClick={setActiveTab.bind(null, index)}
            data-hook={DATA_HOOKS.WIDGET_CATEGORIES_VERTICAL_ITEM(index)}
            href={seo ? category.href : undefined}
          >
            {category.title}
          </TextButton>
        );
      })}
    </div>
  );
};

export default VerticalCategories;
